/**
 * Durable Workbuddy Agent runs: the wire contract between jms-web, jms-api and jms-ai's thread
 * routes (send, subscribe, stop, status, approve, decline).
 *
 * jms-ai cannot import this package, so these shapes mirror by hand what its thread modules
 * declare (`src/mastra/threads/*.ts` and `agents/workbuddy-agent/run.lifecycle.constants.ts`).
 * Change both sides together.
 */

import { AIAgentClientContext, AIInterviewResponse } from './agent.context.model';

/** The only agent the durable routes accept on the wire; the durable wrapper id never appears here. */
export const AI_DURABLE_AGENT_ID = 'workbuddy-agent';

/** Why a run ended, persisted as metadata on the final assistant message; `interrupted` is a run a deploy asked to wrap up. */
export const AI_DURABLE_RUN_COMPLETION_REASONS = ['completed', 'stopped', 'step_limit', 'time_limit', 'failed', 'expired', 'interrupted'] as const;
export type AIDurableRunCompletionReason = typeof AI_DURABLE_RUN_COMPLETION_REASONS[number];

/** Metadata key on the final assistant message that carries the completion record. */
export const AI_DURABLE_RUN_COMPLETION_METADATA_KEY = 'workbuddyCompletion';

export interface AIDurableRunCompletion {
    schemaVersion: 1;
    reason: AIDurableRunCompletionReason;
    /** The loop's own finish reason (stop, tool-calls, abort, error, ...), kept for diagnostics. */
    finishReason: string;
    stepCount: number;
    endedAt: string;
}

export function isAIDurableRunCompletionReason(value: unknown): value is AIDurableRunCompletionReason {

    return typeof value === 'string' && AI_DURABLE_RUN_COMPLETION_REASONS.some(reason => reason === value);
}

/** Derived thread state: a card waits, a run executes, an orphan or aged card was finalized, or nothing is going on. */
export const AI_DURABLE_THREAD_STATES = ['waiting', 'running', 'failed', 'expired', 'idle'] as const;
export type AIDurableThreadState = typeof AI_DURABLE_THREAD_STATES[number];

/** One queued steering message, visible in every window until the loop consumes it. */
export interface AIDurablePendingSteeringEntry {
    /** Mastra signal id; equals the id of the `signal` row once the loop persists it. */
    signalId: string;
    text: string;
    /** ISO timestamp of route acceptance. */
    acceptedAt: string;
}

/** GET .../threads/:threadId/status */
export interface AIDurableThreadStatusResponse {
    state: AIDurableThreadState;
    runId?: string;
    /** Completion reason of the last ended turn (idle), or the reason just written (expired, failed). */
    reason?: AIDurableRunCompletionReason;
    suspendedAt?: string;
    expiresAt?: string;
    toolCallIds?: string[];
    /** Queued steering visible in every window, healed against the transcript and the expiry. */
    pendingSteering: AIDurablePendingSteeringEntry[];
    /** Steering returned to the composer because the run it targeted was finalized as failed. */
    unconsumedSteering?: AIDurablePendingSteeringEntry[];
    /** False while Redis cannot be read; `running` is then a storage-only view. */
    coordinationAvailable: boolean;
}

/** What the sending window believed: idle thread (`start`) or running agent (`steer`). */
export type AIDurableSendIntent = 'start' | 'steer';

/** POST .../threads/:threadId/send body. Text only; attachments ride the same context fields the stream route uses. */
export type AIDurableSendRequestBody = AIAgentClientContext & {
    message: string;
    intent?: AIDurableSendIntent;
};

export interface AIDurableSendResponse {
    mode: 'started' | 'steered';
    runId: string;
    /** The persisted id of the user message (start) or of the steering signal row (steer). */
    messageId: string;
}

/** POST .../threads/:threadId/stop */
export interface AIDurableStopResponse {
    /** True once no run on the thread is executing anywhere. */
    stopped: boolean;
    abortedRunIds: string[];
    /** Suspended runs cancelled quietly because the user stopped while a card waited. */
    cancelledSuspendedRunIds: string[];
    /** Steering the loop never consumed, in accepted order, for the composer. */
    unconsumed: AIDurablePendingSteeringEntry[];
}

/** POST .../threads/:threadId/approve body. */
export interface AIDurableApproveRequestBody {
    /** Narrows to one card; required only when a run holds several suspended tool calls. */
    toolCallId?: string;
    /** Interview answers when the card is an interview; forwarded to the resumed run's request context. */
    interviewResponse?: AIInterviewResponse;
}

/** POST .../threads/:threadId/decline body. */
export interface AIDurableDeclineRequestBody {
    toolCallId?: string;
    /** Decline reason shown to the model. */
    reason?: string;
}

export interface AIDurableCardAnswerResponse {
    accepted: true;
    runId: string;
    toolCallId?: string;
}

/** Error codes jms-ai's thread routes answer with; jms-api keeps the code and replaces the text. */
export const AI_DURABLE_ROUTE_ERROR_CODES = ['DECIDE_FIRST', 'AGENT_THREAD_LEASE_CONFLICT', 'REDIS_UNAVAILABLE', 'RUNTIME_DRAINING', 'DURABLE_RUNTIME_UNAVAILABLE', 'CARD_EXPIRED', 'NO_WAITING_CARD', 'AMBIGUOUS_CARD'] as const;
export type AIDurableRouteErrorCode = typeof AI_DURABLE_ROUTE_ERROR_CODES[number];

export function isAIDurableRouteErrorCode(value: unknown): value is AIDurableRouteErrorCode {

    return typeof value === 'string' && AI_DURABLE_ROUTE_ERROR_CODES.some(code => code === value);
}

/** Error body every durable route returns from jms-api: calm user copy plus the upstream code for the browser. */
export interface AIDurableRouteError {
    message: string;
    code: AIDurableRouteErrorCode | string;
}
